import type { ContactDetailKey, ContactSection, FooterSettings, HeaderSettings, PageDesign, SectionDesign, Section, SiteDocument, SitePage, Theme } from "./types";
import { pagePath, realPageHref } from "./utils";

export const contactDetailKeys: ContactDetailKey[] = ["phone", "email", "address", "hours"];

export function safeHref(value?: string): string {
  const href = value?.trim() || "";
  return /^(?:https?:\/\/[^\s]+|mailto:[^\s]+|tel:[+0-9() .-]+|\/(?!\/)[^\s]*|#[^\s]*)$/i.test(href) ? href : "#";
}

export function contrastRatio(first: string, second: string): number {
  const luminance = (hex: string) => {
    const rgb = [1, 3, 5].map(index => parseInt(hex.slice(index, index + 2), 16) / 255)
      .map(channel => channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4);
    return rgb[0]! * 0.2126 + rgb[1]! * 0.7152 + rgb[2]! * 0.0722;
  };
  const a = luminance(first), b = luminance(second);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

export function contrastingInk(background: string) {
  return contrastRatio(background, "#FFFFFF") >= contrastRatio(background, "#111827") ? "#FFFFFF" : "#111827";
}

export function themeStyle(theme: Theme): Record<string, string | number> {
  const radius = { sharp: "0px", soft: "12px", round: "28px" }[theme.radius];
  return {
    "--primary": theme.primary, "--secondary": theme.secondary, "--accent": theme.accent,
    "--background": theme.background, "--site-background": theme.background, "--surface": theme.surface, "--text": theme.text, "--muted": theme.muted,
    "--p": theme.primary, "--s": theme.secondary, "--a": theme.accent, "--bg": theme.background,
    "--radius": radius, "--heading": `"${theme.headingFont}"`, "--body": `"${theme.bodyFont}"`,
    "--heading-fallback": ["Georgia", "Playfair Display"].includes(theme.headingFont) ? "Georgia" : "Arial",
    "--content-width": { narrow: "960px", standard: "1180px", wide: "1440px" }[theme.contentWidth || "standard"],
    "--section-space": { compact: "64px", comfortable: "104px", spacious: "144px" }[theme.spacing || "comfortable"],
    "--text-scale": { small: "0.9", medium: "1", large: "1.12" }[theme.textScale || "medium"],
    "--button-radius": theme.buttonShape ? { rectangle: "0px", rounded: "12px", pill: "999px" }[theme.buttonShape] : radius,
    "--button-ink": contrastingInk(theme.primary), "--accent-ink": contrastingInk(theme.accent),
    "--footer-background": `color-mix(in srgb, ${theme.primary} 20%, #111827)`,
    ...appearanceStyle(theme)
  };
}

function appearanceStyle(theme: Theme): Record<string, string | number> {
  const button = theme.buttonBackground || theme.primary;
  const ink = theme.buttonStyle === "outline" ? button : theme.buttonStyle === "glass" ? theme.text : contrastingInk(button);
  return {
    "--heading-color": theme.headingColor || theme.text, "--link-color": theme.linkColor || theme.primary,
    "--button-bg": button, "--button-color": theme.buttonTextColor || ink, "--button-border": theme.buttonBorderColor || button,
    "--button-hover-bg": theme.buttonHoverBackground || `color-mix(in srgb, ${button} 85%, #111827)`,
    "--button-hover-color": theme.buttonHoverTextColor || contrastingInk(theme.buttonHoverBackground || button),
    "--secondary-button-bg": theme.secondaryButtonBackground || theme.secondary,
    "--secondary-button-color": theme.secondaryButtonTextColor || theme.linkColor || theme.primary,
    "--secondary-button-border": theme.secondaryButtonBorderColor || theme.linkColor || theme.primary,
    "--card-border": theme.borderColor || `color-mix(in srgb, ${theme.text} 16%, transparent)`,
    ...(theme.borderWidth !== undefined ? { "--card-border-width": `${theme.borderWidth}px` } : {}),
    ...(theme.headingWeight !== undefined ? { "--heading-weight": theme.headingWeight } : {}),
    ...(theme.bodyWeight !== undefined ? { "--body-weight": theme.bodyWeight } : {}),
    ...(theme.headingLetterSpacing !== undefined ? { "--heading-letter-spacing": `${theme.headingLetterSpacing}px` } : {}),
    ...(theme.bodyLineHeight !== undefined ? { "--body-line-height": theme.bodyLineHeight } : {}),
    ...(theme.buttonFontSize !== undefined ? { "--button-font-size": `${theme.buttonFontSize}px` } : {})
  };
}

/** "inherit" and null preserve the parent; defaults/reset never mutate its data. */
export function mergeDesign(parent?: PageDesign, child?: SectionDesign): SectionDesign {
  return Object.fromEntries([parent, child].flatMap(value => Object.entries(value || {}).filter(([, item]) => item !== undefined && item !== null && item !== "inherit")));
}

export function applyThemeDesign(theme: Theme, design?: PageDesign): Theme {
  const next = { ...theme };
  const mapping: Record<string, keyof Theme> = { headingFont: "headingFont", bodyFont: "bodyFont", textColor: "text", mutedColor: "muted", surfaceColor: "surface", accentColor: "accent", primaryColor: "primary", secondaryColor: "secondary", effect: "surfaceEffect", radius: "radius", buttonStyle: "buttonStyle", buttonShape: "buttonShape", contentWidth: "contentWidth", spacing: "spacing", textScale: "textScale", motion: "motion", imageShape: "imageShape" };
  const extras = ["headingColor", "linkColor", "buttonBackground", "buttonTextColor", "buttonBorderColor", "buttonHoverBackground", "buttonHoverTextColor", "secondaryButtonBackground", "secondaryButtonTextColor", "secondaryButtonBorderColor", "secondaryButtonStyle", "headingWeight", "bodyWeight", "headingLetterSpacing", "bodyLineHeight", "buttonFontSize", "borderColor", "borderWidth"];
  for (const [key, value] of Object.entries(design || {})) {
    if (value === undefined || value === null || value === "inherit") continue;
    const target = mapping[key] || (extras.includes(key) ? key as keyof Theme : undefined);
    if (target) Object.assign(next, { [target]: value });
  }
  return next;
}

export function pageTheme(theme: Theme, page: Pick<SitePage, "design">): Theme {
  const next = applyThemeDesign(theme, page.design), background = designBackground(page.design || {}, next);
  return background ? { ...next, background } : next;
}

function designBackground(design: PageDesign, theme: Theme) {
  return design.background === "custom" || design.background === "gradient" ? design.backgroundColor || theme.background
    : design.background && design.background !== "default" ? theme[design.background] : undefined;
}

export function websiteClasses(theme: Theme) {
  return `website-design buttons-${theme.buttonStyle} effect-${theme.surfaceEffect || "flat"} images-${theme.imageShape || "rounded"} motion-${theme.motion || "none"}`;
}

export function headerStyle(header: HeaderSettings | undefined, theme: Theme): Record<string, string | number> {
  return { ...(header?.contactFontSize ? { "--header-contact-size": `${header.contactFontSize}px`, "--header-contact-icon-size": `${header.contactFontSize}px` } : {}), ...themeStyle({ ...theme, surface: header?.backgroundColor || theme.surface, text: header?.textColor || theme.text, linkColor: header?.linkColor || header?.textColor || theme.linkColor, buttonBackground: header?.buttonBackground || theme.buttonBackground, buttonTextColor: header?.buttonTextColor || theme.buttonTextColor, buttonBorderColor: header?.buttonBorderColor || theme.buttonBorderColor }) };
}

export function footerStyle(footer: FooterSettings | undefined, theme: Theme): Record<string, string | number> {
  const ink = footer?.textColor || (footer?.appearance === "light" ? theme.text : "#FFFFFF");
  return { "--footer-background": footer?.backgroundColor || (footer?.appearance === "light" ? theme.surface : `color-mix(in srgb, ${theme.primary} 20%, #111827)`), "--footer-ink": ink, "--footer-muted": footer?.textColor || (footer?.appearance === "light" ? theme.muted : `color-mix(in srgb, ${ink} 75%, transparent)`), "--footer-link": footer?.linkColor || ink };
}

export function sectionClasses(section: Section, theme: Theme, page?: Pick<SitePage, "design">) {
  const design = mergeDesign(page?.design, section.design);
  theme = applyThemeDesign(theme, design);
  const effect = !design.effect || design.effect === "inherit" ? theme.surfaceEffect || "flat" : design.effect;
  const shape = !design.imageShape || design.imageShape === "inherit" ? theme.imageShape || "rounded" : design.imageShape;
  return [
    "designed-section", `section-${section.type}`, `section-effect-${effect}`, `section-images-${shape}`, `section-background-${design.background || "default"}`,
    `section-buttons-${theme.buttonStyle}`, `section-secondary-${theme.secondaryButtonStyle || "link"}`, `section-motion-${theme.motion || "none"}`,
    `section-layout-${design.layout || "default"}`, `section-divider-${design.divider || "none"}`,
    design.background && design.background !== "default" ? "section-custom-background" : "",
    design.align ? `section-align-${design.align}` : ""
  ].filter(Boolean).join(" ");
}

export function sectionStyle(section: Section, theme: Theme, page?: Pick<SitePage, "design">): Record<string, string | number> {
  const design = mergeDesign(page?.design, section.design);
  const parentBackground = page ? pageTheme(theme, page).background : theme.background;
  theme = applyThemeDesign(theme, design);
  const style: Record<string, string | number> = themeStyle(theme);
  style["--site-background"] = parentBackground;
  const background = designBackground(design, theme);
  if (background) Object.assign(style, { "--section-bg": background, "--background": background, "--bg": background });
  if (design.background === "gradient") style["--section-gradient"] = `linear-gradient(${design.gradientAngle ?? 135}deg, ${background}, ${design.gradientColor || theme.secondary})`;
  const text = design.textColor || (design.background === "primary" || (section.type === "cta" && (!design.background || design.background === "default")) ? contrastingInk(theme.primary) : undefined);
  if (text) Object.assign(style, { "--text": text, "--muted": text });
  if (design.mutedColor) style["--muted"] = design.mutedColor;
  style["--heading-color"] = theme.headingColor || text || theme.text;
  style["--link-color"] = theme.linkColor || text || theme.primary;
  if (design.background === "primary" && !design.surfaceColor) style["--surface"] = `color-mix(in srgb, ${theme.primary} 90%, ${contrastingInk(theme.primary)} 10%)`;
  if (design.spacing && design.spacing !== "inherit") style["--section-space"] = { compact: "64px", comfortable: "104px", spacious: "144px" }[design.spacing];
  if (design.columns) {
    style["--columns"] = design.columns;
    style["--tablet-columns"] = Math.min(design.columns, 2);
  }
  if (design.imagePosition) style["--image-position"] = design.imagePosition;
  const button = theme.buttonBackground || (section.type === "cta" || design.background === "primary" ? theme.accent : theme.primary);
  Object.assign(style, { "--button-bg": button, "--button-color": theme.buttonTextColor || (theme.buttonStyle === "glass" ? text || theme.text : contrastingInk(button)), "--button-border": theme.buttonBorderColor || button, "--button-hover-bg": theme.buttonHoverBackground || `color-mix(in srgb, ${button} 85%, #111827)`, "--button-hover-color": theme.buttonHoverTextColor || contrastingInk(theme.buttonHoverBackground || button) });
  if (!theme.buttonTextColor && theme.buttonStyle === "outline") style["--button-color"] = theme.buttonBackground || (text ? text : theme.primary);
  return style;
}

export function orderedContactDetails(section: ContactSection) {
  const order = [...(section.detailsOrder || []), ...contactDetailKeys];
  return [...new Set(order)].filter(key => section[key]).map(key => ({ key, value: section[key]! }));
}

export function siteContact(site: Pick<SiteDocument, "pages">): ContactSection | undefined {
  const section = site.pages.flatMap(page => page.sections).find(section => section.type === "contact" && section.visible);
  return section?.type === "contact" ? section : undefined;
}

export function contactHref(site: Pick<SiteDocument, "pages">): string | undefined {
  if (site.pages.some(page => page.slug === "contact")) return "/contact";
  for (const page of site.pages) {
    const section = page.sections.find(section => section.type === "contact" && section.visible);
    if (section) return `${pagePath(page) === "/" ? "/" : pagePath(page)}#${section.id}`;
  }
  return undefined;
}

/** Resolve legacy type anchors as well as section IDs, including sections moved to other pages. */
export function resolveSiteHref(site: Pick<SiteDocument, "pages">, current: SitePage, href?: string, basePath = "") {
  let target = realPageHref(site, safeHref(href));
  if (target.startsWith("#") && target.length > 1) {
    const anchor = target.slice(1);
    const candidates = [current, ...site.pages.filter(page => page.id !== current.id)];
    const exact = candidates.find(page => page.sections.some(section => section.visible && section.id === anchor));
    const page = exact || candidates.find(page => page.sections.some(section => section.visible && section.type === anchor));
    const section = page?.sections.find(section => section.visible && (section.id === anchor || (!exact && section.type === anchor)));
    if (page && section) target = `${pagePath(page)}#${section.id}`;
  }
  return basePath && target.startsWith("/") ? `${basePath}${target === "/" ? "" : target.startsWith("/#") ? target.slice(1) : target}` : target;
}

export function moveSectionToPage(site: SiteDocument, sourceId: string, sectionId: string, targetId: string): SiteDocument {
  const source = site.pages.find(page => page.id === sourceId);
  const target = site.pages.find(page => page.id === targetId);
  const section = source?.sections.find(section => section.id === sectionId);
  if (!source || !target || !section || sourceId === targetId || target.sections.length >= 30) return site;
  const previousLink = `${pagePath(source)}#${sectionId}`;
  const nextLink = `${pagePath(target)}#${sectionId}`;
  const moved = {
    ...site,
    pages: site.pages.map(page => page.id === sourceId ? { ...page, sections: page.sections.filter(item => item.id !== sectionId) }
      : page.id === targetId ? { ...page, sections: [...page.sections, section] } : page)
  };
  return mapSiteLinks(moved, href => href === previousLink ? nextLink : href);
}

function mapSiteLinks(site: SiteDocument, rewrite: (href: string) => string): SiteDocument {
  return {
    ...site,
    ...(site.header ? { header: { ...site.header, ...(site.header.ctaHref !== undefined ? { ctaHref: rewrite(site.header.ctaHref) } : {}) } } : {}),
    navigation: site.navigation.map(link => ({ ...link, href: rewrite(link.href) })),
    pages: site.pages.map(page => ({ ...page, sections: page.sections.map(section =>
      section.type === "hero" ? { ...section, primaryHref: rewrite(section.primaryHref), ...(section.secondaryHref !== undefined ? { secondaryHref: rewrite(section.secondaryHref) } : {}) }
        : section.type === "cta" ? { ...section, href: rewrite(section.href) } : section
    ) }))
  };
}

export function renamePagePath(site: SiteDocument, pageId: string, slug: string): SiteDocument {
  if (["privacy-policy", "terms-of-use", "cookie-policy"].includes(slug) && !site.pages.some(page => page.id === pageId && page.slug === slug)) return site;
  const page = site.pages.find(page => page.id === pageId);
  if (!page || page.id === site.pages[0]?.id || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) || site.pages.some(item => item.id !== pageId && item.slug === slug)) return site;
  const oldPath = pagePath(page), newPath = "/" + slug;
  const renamed = { ...site, pages: site.pages.map(item => item.id === pageId ? { ...item, slug } : item) };
  return mapSiteLinks(renamed, href => href === oldPath || href.startsWith(oldPath + "#") ? newPath + href.slice(oldPath.length) : href);
}
